import { isAboveCD, getQualityTier, getQualityDetail } from '../hooks/useAudioQuality';

/**
 * normalizeAlbum(rawAlbum, source)
 *
 * Accepts raw album data from ANY source (Qobuz, local library, Maestro DB,
 * search results, Valet recommendations, etc.) and returns a consistent shape
 * that AlbumDetailView can rely on — no special-casing inside the component.
 */

function flattenGenreObject(obj) {
  const out = [];
  if (!obj || typeof obj !== 'object') return out;
  const main = obj.name || obj.tag;
  if (main) out.push({ id: obj.id ?? null, name: String(main).trim(), source: null });
  const subs = obj.sub_genres || obj.subgenres || obj.children;
  if (Array.isArray(subs)) {
    for (const sg of subs) {
      if (typeof sg === 'string') {
        out.push({ id: null, name: sg.trim(), source: null });
      } else if (sg && typeof sg === 'object') {
        out.push(...flattenGenreObject(sg));
      }
    }
  }
  return out;
}

function pushArr(out, arr) {
  if (!Array.isArray(arr)) return;
  for (const g of arr) {
    if (typeof g === 'string') out.push({ id: null, name: g.trim(), source: null });
    else if (g && typeof g === 'object') out.push(...flattenGenreObject(g));
  }
}

function extractGenres(raw) {
  const seen = new Set();
  const dedup = (list) => list.filter((g) => {
    if (!g.name) return false;
    const k = g.name.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  const out = [];

  // 1. Primary genre object (Qobuz: { id, name, slug, sub_genres/subgenres: [...] })
  if (raw.genre && typeof raw.genre === 'object' && !Array.isArray(raw.genre)) {
    out.push(...flattenGenreObject(raw.genre));
  } else if (typeof raw.genre === 'string' && raw.genre.trim()) {
    out.push({ id: null, name: raw.genre.trim(), source: null });
  }

  // 2. genres array — checked separately from genres_list
  pushArr(out, raw.genres);

  // 3. genres_list — Qobuz sometimes uses this field
  pushArr(out, raw.genres_list);

  // 4. subgenres / sub_genres at top level
  pushArr(out, raw.subgenres);
  pushArr(out, raw.sub_genres);

  // 5. tags (MusicBrainz / other)
  const tags = raw.tags;
  if (Array.isArray(tags)) {
    for (const t of tags) {
      if (typeof t === 'string') out.push({ id: null, name: t.trim(), source: null });
      else if (t?.name) out.push({ id: t.id ?? null, name: String(t.name).trim(), source: null });
    }
  } else if (typeof tags === 'string') {
    tags.split(/[,;]/).map((s) => s.trim()).filter(Boolean)
      .forEach((name) => out.push({ id: null, name, source: null }));
  }

  // Dev debug: log raw genre data to verify Qobuz returns
  if (process.env.NODE_ENV !== 'production' && (raw.genre || raw.genres || raw.genres_list || raw.subgenres)) {
    console.debug('[extractGenres] raw genre fields:', {
      genre: raw.genre,
      genres: raw.genres,
      genres_list: raw.genres_list,
      subgenres: raw.subgenres,
      sub_genres: raw.sub_genres,
      result_count: out.length,
    });
  }

  return dedup(out);
}

export function normalizeAlbum(rawAlbum, source) {
  if (!rawAlbum) return null;
  const raw = rawAlbum;
  const src = source || raw.source || raw.__source || raw._serviceId || null;

  const id = String(
    raw.album_key || raw.serviceAlbumId || raw.external_id || raw.externalId ||
    raw.musicbrainz_release_id || raw.mbid || raw.id || ''
  ).trim();

  if (!id) {
    console.warn('[normalizeAlbum] album has no derivable identity', { raw });
    return null;
  }

  const title = String(raw.title || raw.name || '').trim() || 'Untitled';

  const artist = String(
    raw.artistName || raw.artist_name || raw.artist ||
    (typeof raw.artist === 'object' ? raw.artist?.name : '') || 'Unknown Artist'
  ).trim();

  const artistId = String(
    raw.artistId || raw.artist_id ||
    (typeof raw.artist === 'object' ? raw.artist?.id : '') || ''
  ).trim() || null;

  const constructedQobuzCover = (() => {
    const qid = String(raw.serviceAlbumId || id).trim();
    if (qid.length < 4) return null;
    if (/^[0-9a-f]{8}-[0-9a-f]{4}/i.test(qid)) return null; // UUID (MusicBrainz, etc.)
    if (/^\d+$/.test(qid)) return null; // pure numeric
    if (qid.includes(':')) return null; // local:xxx or similar namespaced IDs
    return `https://static.qobuz.com/images/covers/${qid.slice(-2)}/${qid.slice(-4, -2)}/${qid}_600.jpg`;
  })();

  const imageUrl =
    raw.coverUrl || raw.cover_url || raw.coverUrlLarge || raw.cover_url_large ||
    raw.cover || raw.artworkUrl || raw.artwork_url || raw.image?.large ||
    raw.image?.thumbnail || constructedQobuzCover || null;

  const releaseYear = (() => {
    const rd = raw.releaseDate || raw.release_date || raw.release_date_original ||
      raw.released_at || raw.release_year || raw.year;
    if (!rd) return null;
    if (typeof rd === 'number') return rd <= 9999 ? rd : null;
    const s = String(rd).trim();
    const m = s.match(/(\d{4})/);
    return m ? Number(m[1]) : null;
  })();

  const trackCount = raw.tracksCount || raw.trackCount || raw.tracks_count ||
    (Array.isArray(raw.tracks) ? raw.tracks.length : 0);

  const duration = raw.duration || raw.duration_ms ||
    (Array.isArray(raw.tracks)
      ? raw.tracks.reduce((sum, t) => sum + (t.duration_ms || t.duration || 0), 0)
      : null);

  const genres = extractGenres(raw);
  if (src) genres.forEach((g) => { if (!g.source) g.source = src; });

  const genre = genres.length > 0 ? genres[0].name : (
    typeof raw.genre === 'string' ? raw.genre :
    typeof raw.genre === 'object' ? raw.genre?.name : null
  );

  const bd = raw.maxBitDepth || raw.maximum_bit_depth || null;
  const sr = raw.maxSampleRate || raw.maximum_sampling_rate || null;
  const hires = isAboveCD(bd, sr);
  const qualityTier = getQualityTier(bd, sr);

  const quality = (() => {
    const detail = getQualityDetail(bd, sr);
    if (detail) return detail;
    return null;
  })();

  const tracks = Array.isArray(raw.tracks) ? raw.tracks
    : (Array.isArray(raw.tracks?.items) ? raw.tracks.items : []);

  // Preserve the raw genre object if it has sub_genres (Qobuz structure)
  const rawGenreObj = (raw.genre && typeof raw.genre === 'object' && !Array.isArray(raw.genre))
    ? raw.genre : null;

  return {
    ...raw,
    id,
    title,
    artist,
    artistName: artist,
    artist_name: artist,
    artistId,
    imageUrl,
    coverUrl: imageUrl,
    cover: imageUrl,
    source: src,
    releaseYear,
    releaseDate: raw.releaseDate || raw.release_date || raw.release_date_original || raw.released_at || null,
    trackCount,
    duration,
    genre: rawGenreObj || genre,
    genres,
    quality,
    tracks,
    hires,
    qualityTier,
    serviceAlbumId: (() => {
      const sid = raw.serviceAlbumId || (src === 'qobuz' ? String(raw.id || '') : null);
      return sid ? String(sid).replace(/^qobuz:album:/, '') : null;
    })(),
    maxBitDepth: raw.maxBitDepth || raw.maximum_bit_depth || null,
    maxSampleRate: raw.maxSampleRate || raw.maximum_sampling_rate || null,
    label: typeof raw.label === 'object' ? raw.label?.name : (raw.label || raw.recordLabel || null),
    album_key: raw.album_key || null,
  };
}

export default normalizeAlbum;
